import {
  TCPropertyImportAssignmentMode as PropertyImportAssignmentMode,
  TCRealEstateProviderId as RealEstateProviderId,
  type TCRealEstateProviderId,
  type TCPropertyImportAssignmentMode,
  type TCPropertyImportFilterCondition,
  type TCPropertyImportOptions,
  type TCPropertyImportSyncMissingPolicy,
} from 'src/api/generated';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';

interface BuildAssignmentModeOptionsArgs {
  assignmentMode: TCPropertyImportAssignmentMode;
  assignmentUserId: number | null;
  filterCondition: TCPropertyImportFilterCondition | null;
  syncMissingPolicy: TCPropertyImportSyncMissingPolicy;
}

const SPREADSHEET_FILE_ACCEPT =
  '.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const TABULAR_FILE_ACCEPT = `.csv,${SPREADSHEET_FILE_ACCEPT},text/csv`;

interface PropertyImportFileFormatConfig {
  /** Browser file-picker accept value. */
  accept: string;
  /** Translation key for the accepted-format description. */
  descriptionKey: TranslationKey;
  /** Translation key for the file field label. */
  labelKey: TranslationKey;
  /** Translation key for the missing-file alert. */
  noFileAlertKey: TranslationKey;
}

/**
 * Describes the file formats exposed by the import modal for a provider.
 */
export const getPropertyImportFileFormatConfig = (
  providerId: TCRealEstateProviderId
): PropertyImportFileFormatConfig =>
  providerId === RealEstateProviderId.GLOBALBROKERS
    ? {
        accept: TABULAR_FILE_ACCEPT,
        descriptionKey: 'catalog.properties.import.description.tabular',
        labelKey: 'catalog.properties.import.file.label.tabular',
        noFileAlertKey: 'catalog.properties.import.alert.noFile.tabular',
      }
    : {
        accept: SPREADSHEET_FILE_ACCEPT,
        descriptionKey: 'catalog.properties.import.description.spreadsheet',
        labelKey: 'catalog.properties.import.file.label.spreadsheet',
        noFileAlertKey: 'catalog.properties.import.alert.noFile.spreadsheet',
      };

/**
 * Builds the property import `options` payload from UI state.
 */
export const buildAssignmentModeOptions = ({
  assignmentMode,
  assignmentUserId,
  filterCondition,
  syncMissingPolicy,
}: BuildAssignmentModeOptionsArgs): TCPropertyImportOptions => {
  const assignment = buildAssignment(assignmentMode, assignmentUserId);
  return { filterCondition, assignment, syncMissingPolicy };
};

/**
 * Builds the assignment configuration based on the selected assignment mode.
 */
const buildAssignment = (
  mode: TCPropertyImportAssignmentMode,
  userId: number | null
) => {
  if (mode === PropertyImportAssignmentMode.KEEP) {
    return null;
  }

  if (mode === PropertyImportAssignmentMode.FORCE_USER) {
    if (!userId) {
      throw new Error(
        'Expected assignmentUserId for FORCE_USER assignment mode'
      );
    }
    return { mode, userId };
  }

  return { mode };
};
